import { useState, useRef, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useAuth } from 'components/context/AuthContext';
import { streamQuery, TOOL_LABELS } from 'api/demoOpus';
import './DemoOpus.css';

const GREETING = '¿Cómo te puedo ayudar hoy?';

const SUGGESTIONS = [
  '¿Cuáles fueron los artículos más vendidos?',
  '¿Cuánto stock tengo de portland?',
  'Ventas por familia',
  'Mostrame la ficha de un cliente',
];

const patchLast = (messages, patch) => {
  if (!messages.length) return messages;
  const last = messages[messages.length - 1];
  return [...messages.slice(0, -1), { ...last, ...patch }];
};

export default function DemoOpus() {
  const { auth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(
    async (raw) => {
      const question = (raw ?? input).trim();
      if (!question || loading) return;
      setInput('');
      setLoading(true);

      const history = messages
        .filter((m) => m.content && !m.error)
        .map((m) => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.content }));

      setMessages((prev) => [
        ...prev,
        { role: 'user', content: question },
        { role: 'bot', content: '', status: null, streaming: true },
      ]);

      try {
        await streamQuery(question, {
          history,
          onStatus: (tool) =>
            setMessages((prev) =>
              patchLast(prev, { status: TOOL_LABELS[tool] || 'los datos' })
            ),
          onDelta: (text) =>
            setMessages((prev) =>
              patchLast(prev, {
                content: (prev[prev.length - 1]?.content || '') + text,
                status: null,
              })
            ),
          onError: (message) =>
            setMessages((prev) =>
              patchLast(prev, { content: message, error: true, status: null })
            ),
        });
      } catch (_e) {
        setMessages((prev) =>
          patchLast(prev, {
            content: 'No pude conectarme. Probá de nuevo en un momento.',
            error: true,
            status: null,
          })
        );
      } finally {
        setMessages((prev) => patchLast(prev, { streaming: false, status: null }));
        setLoading(false);
        inputRef.current?.focus();
      }
    },
    [input, loading, messages]
  );

  if (!auth?.token) return <Navigate to="/login" replace />;

  const hasChat = messages.length > 0;
  const pubUrl = process.env.PUBLIC_URL || '';

  return (
    <div className="opus">
      <header className="opus-topbar">
        <img className="opus-logo" src={`${pubUrl}/opus-logo.svg`} alt="Opus" />
        <div className="opus-poweredby">
          <span>powered by</span>
          <img src={`${pubUrl}/esporai-logo.png`} alt="espor.ai" />
        </div>
      </header>

      <main className={`opus-main ${hasChat ? 'is-chat' : 'is-empty'}`} ref={scrollRef}>
        {!hasChat ? (
          <div className="opus-hero">
            <h1 className="opus-greeting">{GREETING}</h1>
            <div className="opus-composer-wrap">
              <Composer
                input={input}
                setInput={setInput}
                onSend={() => send()}
                loading={loading}
                inputRef={inputRef}
              />
            </div>
            <div className="opus-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} className="opus-chip" onClick={() => send(s)} disabled={loading}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="opus-thread">
            {messages.map((m, i) => (
              <div key={i} className={`opus-msg opus-msg--${m.role}`}>
                {m.role === 'bot' ? (
                  <div className="opus-bubble opus-bubble--bot">
                    {m.status ? (
                      <div className="opus-status">
                        <span className="opus-dot" /> Consultando {m.status}…
                      </div>
                    ) : m.content ? (
                      <div className={`opus-markdown ${m.error ? 'opus-error' : ''}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <div className="opus-status">
                        <span className="opus-dot" /> Pensando…
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="opus-bubble opus-bubble--user">{m.content}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {hasChat && (
        <div className="opus-footer">
          <Composer
            input={input}
            setInput={setInput}
            onSend={() => send()}
            loading={loading}
            inputRef={inputRef}
          />
          <p className="opus-disclaimer">
            Opus responde solo con datos reales del inventario cargado (junio 2026).
          </p>
        </div>
      )}
    </div>
  );
}

function Composer({ input, setInput, onSend, loading, inputRef }) {
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  return (
    <div className="opus-composer">
      <textarea
        ref={inputRef}
        className="opus-input"
        placeholder="Preguntá sobre stock, ventas, clientes, proveedores…"
        value={input}
        rows={1}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button
        className="opus-send"
        onClick={onSend}
        disabled={loading || !input.trim()}
        aria-label="Enviar"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 12h15M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}
